// User Types

export enum UserRole {
  CUSTOMER = 'customer',
  DRIVER = 'driver',
  AGENT = 'agent',
  ADMIN = 'admin',
  HOST = 'host',
  BROKER = 'broker',
  LIAISON = 'liaison',
  FLEET_MANAGER = 'fleet_manager'
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  PENDING = 'pending'
}

export enum VerificationStatus {
  NONE = 'none',
  DRAFT = 'draft',
  PENDING_REVIEW = 'pending_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  EXPIRED = 'expired'
}

export interface User {
  id: string;
  email: string;
  fullName: string;
  phone?: string;
  role: UserRole;
  permissions?: string[]; // Admin permissions from backend (e.g., ["corporate:view", "users:manage"])
  status: UserStatus;
  emailVerified: boolean;
  phoneVerified: boolean;

  // Enhanced verification fields
  isVerified: boolean;
  canRentCars: boolean;
  canHireDrivers: boolean;
  verificationStatus: VerificationStatus;
  verificationId?: string;
  profileCompletionPercentage: number;
  documentsSubmitted: boolean;
  verificationSubmittedAt?: string;
  verificationApprovedAt?: string;
  onboardingCompleted: boolean;

  createdAt: string;
  updatedAt: string;
}

// Unified Multi-Role User Interface
export interface UnifiedUser {
  id: string;
  personalInfo: {
    fullName: string;
    email: string;
    phone?: string;
    location?: string;
    profileImage?: string;
  };

  activeRoles: {
    isRenter: boolean;
    isHost: boolean;
    isDriver: boolean;
    isAdmin: boolean;
  };

  hostProfile?: {
    hostType: 'owner' | 'broker' | 'fleet' | 'liaison' | 'manager';
    totalVehicles: number;
    activeListings: number;
    totalEarnings: number;
    averageRating: number;
    hostStatus: 'pending' | 'active' | 'suspended' | 'rejected';
    hostCode?: string;
    joinedAsHostAt?: string;
  };

  driverProfile?: {
    licenseNumber?: string;
    licenseClass?: string;
    licenseExpiry?: string;
    onboardingStep: number;
    totalTrips: number;
    totalEarnings: number;
    averageRating: number;
    availability: 'available' | 'busy' | 'offline';
    driverStatus: 'pending' | 'active' | 'suspended' | 'rejected';
    driverCode?: string;
    guarantorInfo?: {
      primary?: { name: string; phone: string; };
      secondary?: { name: string; phone: string; };
    };
    joinedAsDriverAt?: string;
  };

  renterProfile?: {
    totalBookings: number;
    totalSpent: number;
    averageRating: number;
    renterStatus: 'active' | 'suspended' | 'blocked';
    preferredVehicleTypes: string[];
    joinedAsRenterAt?: string;
  };

  verification: {
    identity: {
      status: VerificationStatus;
      submittedAt?: string;
      reviewedAt?: string;
      documents: string[];
    };
    documents: {
      status: VerificationStatus;
      submittedAt?: string;
      reviewedAt?: string;
      missingDocuments: string[];
    };
    background: {
      status: VerificationStatus;
      submittedAt?: string;
      reviewedAt?: string;
      notes?: string;
    };
    overall: VerificationStatus;
    completionPercentage: number;
  };

  accountStatus: UserStatus;
  lastActiveAt: string;
  registeredAt: string;

  followUp?: {
    pendingItems: string[];
    lastContactAt?: string;
    urgencyLevel: 'low' | 'medium' | 'high' | 'critical';
    adminNotes: { note: string; addedBy: string; addedAt: string; }[];
  };

  platformMetrics: {
    loginCount: number;
    supportTickets: number;
    disputes: number;
    referrals: number;
  };
}
